import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import type { RelayChatExtension } from '@inference.engine/agentic-react';
import { TAXONOMY_AGENT_PROGRESS_PART } from '@inference.engine/taxonomy/web';
import type { PageAgentChat } from '@/entities/agent';
import { importStateOf, type ImportState } from '@/entities/taxonomy-import';
import { useGetSignedUrl } from '@/shared/hooks/useGetSignedUrl/useGetSignedUrl';

/**
 * The file a page was opened to build from, taken from history once and then cleared from it:
 * browser history keeps route state across a reload, so leaving it would send the same file again
 * every time. A person who comes back later attaches the file in the chat instead.
 */
export function useOpenedFile(recordId: string): ImportState | null {
  const location = useLocation();
  const navigate = useNavigate();
  const inRoute = importStateOf(location.state);
  const [opened] = useState(() => ({ recordId, value: inRoute }));
  const hasRouteState = Boolean(inRoute);
  useEffect(() => {
    if (hasRouteState) void navigate(location.pathname, { replace: true, state: null });
  }, [hasRouteState, location.pathname, navigate]);
  return opened.recordId === recordId ? opened.value : null;
}

/**
 * The conversation's first message, carrying the file the page opened with, fixed once its link
 * has loaded: the chat sends it once, and a refetch must not change it. A link that fails leaves
 * the file shown but not clickable.
 */
export function useFileKickoff(
  agentId: string,
  opened: ImportState | null,
  prompt: string,
): PageAgentChat['initialMessage'] {
  const file = opened?.importFile;
  const signed = useGetSignedUrl(file?.assetId);
  const kickoff = useRef<PageAgentChat['initialMessage']>(undefined);
  if (file && !signed.isLoading && !kickoff.current) {
    kickoff.current = {
      agentId,
      prompt,
      files: [
        {
          assetId: file.assetId,
          mimeType: file.mimeType,
          metadata: { fileName: file.fileName },
          url: signed.downloadUrl ?? '',
        },
      ],
    };
  }
  return kickoff.current;
}

const progressSchema = z.object({ label: z.string() });

/** The step the agent is on while it reads a file or writes rows, for the composer to show. */
export function useAgentProgress(): {
  extension: RelayChatExtension;
  busyLabel: string | undefined;
  onTurnEnd: () => void;
} {
  const [busyLabel, setBusyLabel] = useState<string>();
  const extension = useMemo<RelayChatExtension>(
    () => ({
      part: `data-${TAXONOMY_AGENT_PROGRESS_PART}`,
      onPart: (data) => {
        const parsed = progressSchema.safeParse(data);
        if (parsed.success) setBusyLabel(parsed.data.label);
      },
    }),
    [],
  );
  const onTurnEnd = useCallback(() => setBusyLabel(undefined), []);
  return { extension, busyLabel, onTurnEnd };
}
